import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import StudentGapTask from 'Components/ChecklistTasks/StudentGapTask';
import StudentMathFormulasTask from 'Components/ChecklistTasks/StudentMathFormulasTask';
import StudentTestTask from 'Components/ChecklistTasks/StudentTestTask';
import StudentUnderlineTask from 'Components/ChecklistTasks/StudentUnderlineTask';
import StudentWordCompositionTask from 'Components/ChecklistTasks/StudentWordCompositionTask';
import StudentWordStressTask from 'Components/ChecklistTasks/StudentWordStressTask';

import { hwApi, taskApi } from '../../api';
import { getStudents } from '../../Common/Database';
import StudentExcludesTask from '../../Components/ChecklistTasks/StudentExludesTask';
import StudentSelectOneTask from '../../Components/ChecklistTasks/StudentSelectOneTask';
import StudentSentenceStructureTask from '../../Components/ChecklistTasks/StudentSentenceStructure';
import StudentTaskWithLink from '../../Components/ChecklistTasks/StudentTaskWithLink';
import StudentTextTask from '../../Components/ChecklistTasks/StudentTextTask';
import EditorCanvas from '../../Components/ImageEditor/EditorCanvas';
import EditorProvider from '../../Components/ImageEditor/EditorProvider';
import { LoadingError } from '../../Components/LoadingError';
import { HW, TaskKeysType } from '../../shared/types';
import { SummarizeTask } from '../../shared/types/task';

const componentMapping = {
  text: StudentTextTask,
  link: StudentTaskWithLink,
  test: StudentTestTask,
  exclude: StudentExcludesTask,
  selectOne: StudentSelectOneTask,
  sentenceStructure: StudentSentenceStructureTask,
  gap: StudentGapTask,
  mathFormulas: StudentMathFormulasTask,
  wordComposition: StudentWordCompositionTask,
  wordStress: StudentWordStressTask,
  underline: StudentUnderlineTask,
};

export default function HomeworkTeacher({ preview }: { preview?: boolean }) {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();
  const { state } = useLocation();

  const [hwData, setHwData] = useState<HW | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<SummarizeTask[]>([]);
  const [studentName, setStudentName] = useState<string>('');

  useEffect(() => {
    const isAllTaskChecked = tasks.every(it => Boolean(it.checkedAt));

    if (hwData) {
      hwApi.updateHW({
        ...hwData,
        checked: isAllTaskChecked,
      });
    }
  }, [tasks]);

  useEffect(() => {
    setLoading(true);

    hwApi.getHW(state.hwId).then(data => {
      setHwData(data);

      setLoading(false);
    });
  }, []);

  useEffect(() => {
    const updateTasks = (tasks: SummarizeTask[]) => setTasks(tasks);
    const unsubscribe = taskApi.subscribeToICLTasks({ ICLId: state.hwId, setNewTasks: updateTasks });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (hwData) {
      getStudents().then(students => {
        const student = students.find(s => s.email?.replace(/\./g, '') === hwData.studentEmail);
        if (student) {
          setStudentName(student.FIO);
        }
      });
    }
  }, [hwData]);

  const handleChangeTaskByKey = (id: string, key: TaskKeysType, value: string[] | string | boolean) => {
    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index], [key]: value };

    newTaskArray.splice(index, 1, updatedObject);

    if (updatedObject.checkedAt || !updatedObject.checkedAt) {
      taskApi.updateTask<SummarizeTask>(state.hwId, updatedObject);
    }

    setTasks(newTaskArray);
  };

  const saveTaskAnswer = async (id: string) => {
    setLoading(true);

    const newTaskArray: SummarizeTask[] = [...tasks];
    const index = newTaskArray.findIndex(obj => obj.id === id);

    const updatedObject: SummarizeTask = { ...newTaskArray[index] };

    newTaskArray.splice(index, 1, updatedObject);

    await taskApi.updateTask<SummarizeTask>(state.hwId, updatedObject).then(() => setLoading(false));
  };

  const renderTasks = () => {
    const activeTasks = tasks.filter(it => it.isActive);

    return activeTasks.map((it: SummarizeTask) => {
      const Component = componentMapping[it.type];

      return (
        <Component
          icl={hwData}
          key={it.id}
          {...it}
          handleChangeTaskByKey={handleChangeTaskByKey}
          saveTaskAnswer={saveTaskAnswer}
          hw
          preview={preview}
        />
      );
    });
  };

  return (
    <EditorProvider>
      <LoadingError isLoading={isLoading && !tasks}>
        <div className="mx-auto rounded-[24px] border-x-[8px] border-b-[8px] border-[#9CDFFC7F] border-opacity-50 bg-white px-3 py-4 shadow-none lg:w-11/12 lg:!px-[120px] lg:!py-[40px]">
          <div className="m-auto flex w-fit flex-col gap-[12px] lg:w-full lg:gap-[20px]">
            <div className="text-[24px] font-bold leading-9 lg:text-[48px] lg:leading-[55px]">
              {`${t('title')} ${t('hw')}: `}
              <span>{hwData?.title}</span>
            </div>

            <div className="flex flex-col gap-[12px] lg:flex-row lg:gap-[50%]">
              <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
                {t('class')}: <span className="font-medium">{hwData?.class}</span>
              </div>
            </div>

            <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
              {t('subject')}:{' '}
              <span className="font-medium">
                {platformLang === 'ua' ? hwData?.subject.name : hwData?.subject.ruName}
              </span>
            </div>

            <div className="flex flex-col text-[18px] font-bold leading-6 lg:text-[18px] lg:leading-8">
              {t('Student')}: <span className="font-medium">{studentName}</span>
            </div>
          </div>

          <EditorCanvas icl={hwData} handleChangeTaskByKey={handleChangeTaskByKey} />

          <div>{renderTasks()}</div>
        </div>
      </LoadingError>
    </EditorProvider>
  );
}
